import React, { useState, useContext, useEffect, useRef } from "react";
import {
  ChatBubbleLeftRightIcon,
  XMarkIcon,
  PaperAirplaneIcon,
  ScaleIcon,
  ClockIcon,
} from "@heroicons/react/24/solid";
import { Link } from "react-router-dom";
import { ThemeContext } from "../context/ThemeContext";
import { sendCouncilChat, streamCouncilChat } from "../api/councilApi";
import { useUpgrade } from "./gating/UpgradeContext";

// Council agent metadata used to render attribution chips on assistant turns.
const AGENT_LABELS = {
  anchor: { label: "Anchor", color: "bg-indigo-500/15 text-indigo-500 border-indigo-500/40" },
  compass: { label: "Compass", color: "bg-emerald-500/15 text-emerald-500 border-emerald-500/40" },
  lens: { label: "Lens", color: "bg-sky-500/15 text-sky-500 border-sky-500/40" },
  vigil: { label: "Vigil", color: "bg-rose-500/15 text-rose-500 border-rose-500/40" },
  tally: { label: "Tally", color: "bg-amber-500/15 text-amber-500 border-amber-500/40" },
  terra: { label: "Terra", color: "bg-stone-500/15 text-stone-500 border-stone-500/40" },
};

const TOOL_LABELS = {
  read_ledger: "your ledger",
  fetch_quote: "live quote",
  fetch_news: "news",
  fetch_fundamentals: "fundamentals",
  tax_rules: "tax rules",
  tax_loss_harvest_candidates: "loss-harvest scan",
  compute_concentration: "concentration",
  compute_drawdown: "drawdown",
  compute_var: "VaR",
  compute_stress_test: "stress test",
  compute_goal_progress: "goal progress",
  read_manual_assets: "manual assets",
  compute_real_estate_metrics: "real-estate metrics",
  fetch_real_estate_comparable: "market comps",
  consult_compass: "→ Compass",
  consult_lens: "→ Lens",
  consult_vigil: "→ Vigil",
  consult_tally: "→ Tally",
  consult_terra: "→ Terra",
};

const INTRO_MESSAGE = {
  id: "intro",
  role: "assistant",
  content:
    "Hi — I'm FinAI's Council. Ask about a stock and Lens jumps in. Portfolio? Compass. Tax? Tally. Risk? Vigil. Real estate, land, FDs, gold? Terra. They consult each other when a question crosses domains.",
  agentsUsed: ["anchor"],
  citations: [],
};

const AgentChips = ({ agentsUsed }) => {
  if (!agentsUsed || agentsUsed.length === 0) return null;
  const specialists = agentsUsed.filter((a) => a !== "anchor");
  const display = specialists.length > 0 ? specialists : ["anchor"];
  return (
    <div className="flex flex-wrap gap-1.5 mt-1.5">
      {display.map((a) => {
        const meta = AGENT_LABELS[a] || {
          label: a,
          color: "bg-gray-500/15 text-gray-500 border-gray-500/40",
        };
        return (
          <span
            key={a}
            className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full border ${meta.color}`}
          >
            {meta.label}
          </span>
        );
      })}
    </div>
  );
};

const ToolTrail = ({ citations }) => {
  if (!citations) return null;
  const dataTools = citations.filter(
    (c) => c.tool && !c.tool.startsWith("ask_"),
  );
  if (dataTools.length === 0) return null;
  const labels = [
    ...new Set(dataTools.map((d) => TOOL_LABELS[d.tool] || d.tool)),
  ];
  return (
    <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-1">
      ↳ used {labels.join(" · ")}
    </p>
  );
};

const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const { theme } = useContext(ThemeContext);
  const { promptUpgrade } = useUpgrade();

  const [messages, setMessages] = useState([INTRO_MESSAGE]);
  const [conversationId, setConversationId] = useState(null);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [debate, setDebate] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending]);

  // Listen for "council:loadConversation" events from the conversation
  // history page. Hydrate the widget with the existing turn-by-turn
  // history so the user can pick up where they left off.
  useEffect(() => {
    const handler = (event) => {
      const detail = event?.detail || {};
      if (!detail.conversation_id) return;
      setOpen(true);
      setConversationId(detail.conversation_id);
      const hydrated = (detail.messages || []).map((m, i) => {
        if (m.role === "user") {
          return { id: `u-${m.id || i}`, role: "user", content: m.content };
        }
        // Assistant turns may have stored debate panel data inside citations
        // via the kind:'debate' envelope.
        const debateEnv = (m.citations || []).find((c) => c?.kind === "debate");
        const fightEnv = (m.citations || []).find((c) => c?.kind === "fight");
        return {
          id: `a-${m.id || i}`,
          role: "assistant",
          content: m.content,
          agentsUsed: m.agent ? [m.agent] : ["anchor"],
          citations: m.citations || [],
          debate: debateEnv?.specialists || null,
          fight: fightEnv?.rounds ? { rounds: fightEnv.rounds } : null,
          fightMode: !!fightEnv,
        };
      });
      setMessages(hydrated.length ? hydrated : [INTRO_MESSAGE]);
    };
    window.addEventListener("council:loadConversation", handler);
    return () => window.removeEventListener("council:loadConversation", handler);
  }, []);

  // Listen for "council:askPrefill" events fired by other parts of the app
  // (e.g. the TodayBriefCard's "Ask the Council why" button). The event
  // detail carries a string to send as the next user turn.
  useEffect(() => {
    const handler = (event) => {
      const prefill = event?.detail?.prefill;
      if (!prefill) return;
      setOpen(true);
      // Use a microtask so the widget mounts before we dispatch the send.
      Promise.resolve().then(() => {
        // Inline send to avoid stale-closure issues with the existing onSend
        const userMsg = { id: `u-${Date.now()}`, role: "user", content: prefill };
        setMessages((cur) => [...cur, userMsg]);
        setSending(true);
        sendCouncilChat({ message: prefill, conversationId })
          .then((res) => {
            const data = res.data || {};
            if (data.conversation_id && data.conversation_id !== conversationId) {
              setConversationId(data.conversation_id);
            }
            setMessages((cur) => [
              ...cur,
              {
                id: `a-${data.message_id || Date.now()}`,
                role: "assistant",
                content: data.answer || "(no response)",
                agentsUsed: data.agents_used || ["anchor"],
                citations: data.citations || [],
              },
            ]);
          })
          .catch(() => {
            setMessages((cur) => [
              ...cur,
              {
                id: `a-${Date.now()}`,
                role: "assistant",
                content: "Sorry, I couldn't reach the Council. Try again in a moment.",
                agentsUsed: ["anchor"],
                citations: [],
              },
            ]);
          })
          .finally(() => setSending(false));
      });
    };
    window.addEventListener("council:askPrefill", handler);
    return () => window.removeEventListener("council:askPrefill", handler);
  }, [conversationId]);

  const bg = theme === "dark" ? "bg-[#111827]" : "bg-white";
  const text = theme === "dark" ? "text-white" : "text-gray-900";
  const border = theme === "dark" ? "border-gray-700" : "border-gray-300";
  const inputBg = theme === "dark" ? "bg-gray-800" : "bg-gray-100";
  const inputBorder = theme === "dark" ? "border-gray-600" : "border-gray-300";
  const userBubble = "bg-indigo-600 text-white";
  const aiBubble =
    theme === "dark"
      ? "bg-gray-800/70 border border-gray-700 text-white"
      : "bg-gray-100 border border-gray-200 text-gray-900";

  const onSend = async (event) => {
    event?.preventDefault();
    const trimmed = input.trim();
    if (!trimmed || sending) return;
    const userMsg = {
      id: `u-${Date.now()}`,
      role: "user",
      content: trimmed,
    };
    setMessages((cur) => [...cur, userMsg]);
    setInput("");
    setSending(true);
    // Always stream. The backend decides whether to run a normal answer or a
    // multi-round "fight" (smart-auto); `debate` forces the fight.
    const forceDebate = debate;
    try {
      const placeholderId = `a-stream-${Date.now()}`;
      setMessages((cur) => [
        ...cur,
        {
          id: placeholderId,
          role: "assistant",
          content: "",
          agentsUsed: ["anchor"],
          citations: [],
          streaming: true,
          fight: null,
          fightMode: false,
          fightStatus: "",
        },
      ]);

      const mutate = (fn) =>
        setMessages((cur) => cur.map((m) => (m.id === placeholderId ? fn(m) : m)));

      const finalPayload = await streamCouncilChat({
        message: trimmed,
        conversationId,
        debate: forceDebate,
        onEvent: (type, payload) => {
          if (type === "mode" && payload?.mode === "fight") {
            mutate((m) => ({
              ...m,
              fightMode: true,
              fightStatus: "Convening the Council…",
              fight: {
                rounds: [
                  { phase: "opening", takes: [] },
                  { phase: "rebuttal", takes: [] },
                ],
              },
            }));
          } else if (type === "phase") {
            const label =
              payload?.phase === "opening"
                ? "Opening takes…"
                : payload?.phase === "rebuttal"
                ? "Rebuttals…"
                : payload?.phase === "verdict"
                ? "Anchor is deciding…"
                : "";
            mutate((m) => ({ ...m, fightStatus: label }));
          } else if (type === "agent_start" && payload?.phase) {
            const who = (payload.agent || "").replace(/^\w/, (c) => c.toUpperCase());
            mutate((m) => ({
              ...m,
              fightStatus: `${who} ${payload.phase === "rebuttal" ? "rebutting" : "weighing in"}…`,
            }));
          } else if (type === "specialist_take") {
            mutate((m) => {
              if (!m.fight) return m;
              const rounds = m.fight.rounds.map((r) =>
                r.phase === payload.phase
                  ? { ...r, takes: [...r.takes, { agent: payload.agent, answer: payload.text || "" }] }
                  : r,
              );
              return { ...m, fight: { rounds } };
            });
          } else if (type === "delta") {
            const chunk = payload?.content || "";
            if (!chunk) return;
            mutate((m) => ({ ...m, content: (m.content || "") + chunk, fightStatus: "" }));
          } else if (type === "tool") {
            mutate((m) => ({
              ...m,
              citations: [
                ...(m.citations || []),
                { kind: "tool", tool: payload?.tool, args: payload?.args },
              ],
            }));
          }
        },
      });

      if (finalPayload?.conversation_id && finalPayload.conversation_id !== conversationId) {
        setConversationId(finalPayload.conversation_id);
      }
      mutate((m) => ({
        ...m,
        id: `a-${finalPayload?.message_id || Date.now()}`,
        content: finalPayload?.answer || m.content || "(no response)",
        agentsUsed: finalPayload?.agents_used || ["anchor"],
        citations: finalPayload?.citations || m.citations || [],
        fight: finalPayload?.fight?.rounds ? { rounds: finalPayload.fight.rounds } : m.fight,
        fightStatus: "",
        streaming: false,
      }));
      if (forceDebate) setDebate(false);
    } catch (err) {
      // If a streaming placeholder is still in the list (no deltas arrived
      // before the failure), replace it in place rather than leaving an
      // empty bubble plus a separate error bubble.
      // 402 can come from axios (err.response) or the stream client (err.status).
      const status = err.response?.status || err.status;
      const upgradeInfo = err.response?.data || err.data;
      if (status === 402 && upgradeInfo?.upgrade_required) {
        promptUpgrade(upgradeInfo.feature || "ai_daily_limit");
      }
      const errorContent =
        status === 401
          ? "Sign in to chat with the Council."
          : status === 402
          ? "You've reached today's AI message limit on your plan — upgrade for more."
          : "Sorry, I couldn't reach the Council. Try again in a moment.";
      setMessages((cur) => {
        const hasEmptyStreamingBubble = cur.some(
          (m) => m.streaming && (!m.content || m.content.length === 0),
        );
        if (hasEmptyStreamingBubble) {
          return cur.map((m) =>
            m.streaming && (!m.content || m.content.length === 0)
              ? { ...m, content: errorContent, streaming: false }
              : m,
          );
        }
        return [
          ...cur,
          {
            id: `a-${Date.now()}`,
            role: "assistant",
            content: errorContent,
            agentsUsed: ["anchor"],
            citations: [],
          },
        ];
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {!open && (
        <div className="fixed bottom-6 right-6 z-50">
          <button
            onClick={() => setOpen(true)}
            className="glow-border inline-block rounded-full"
            aria-label="Open Council chat"
          >
            <span className="relative flex items-center justify-center w-14 h-14 rounded-full bg-black">
              <ChatBubbleLeftRightIcon className="w-7 h-7 text-white" />
            </span>
          </button>
        </div>
      )}

      {open && (
        <div
          className={`fixed bottom-6 right-6 w-[440px] max-w-[calc(100vw-1.5rem)] max-h-[calc(100dvh-3rem)] ${bg} ${text} ${border} rounded-xl shadow-2xl z-50 animate-fadeIn flex flex-col`}
        >
          <div className={`flex items-center justify-between px-4 py-3 border-b ${border}`}>
            <div>
              <h2 className="text-base font-semibold">The Council</h2>
              <p className="text-[10px] text-gray-400">
                Anchor · Compass · Lens · Vigil · Tally · Terra
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Link
                to="/insights/chats"
                onClick={() => setOpen(false)}
                title="Conversation history"
                className="text-gray-500 hover:text-indigo-500 transition"
              >
                <ClockIcon className="w-5 h-5" />
              </Link>
              <button
                onClick={() => setOpen(false)}
                className="hover:text-red-500 transition"
                aria-label="Close chat"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>
          </div>

          <div
            ref={scrollRef}
            className="flex-1 min-h-0 h-[520px] max-h-[520px] overflow-y-auto p-4 space-y-3 text-sm"
          >
            {messages.map((m) =>
              m.role === "user" ? (
                <div key={m.id} className="flex justify-end">
                  <div
                    className={`max-w-[80%] px-3 py-2 rounded-2xl rounded-br-sm ${userBubble}`}
                  >
                    {m.content}
                  </div>
                </div>
              ) : (
                <div key={m.id} className="max-w-[88%] space-y-2">
                  {/* Fight timeline: opening takes -> rebuttals -> verdict (m.content) */}
                  {(m.fight || m.fightMode) && (
                    <div className="space-y-2">
                      <p className="text-[10px] font-bold uppercase tracking-widest text-indigo-500 flex items-center gap-1">
                        <ScaleIcon className="w-3 h-3" />
                        The Council debated
                      </p>
                      {(m.fight?.rounds || []).map((round) =>
                        (round.takes || []).length > 0 ? (
                          <div key={`${m.id}-${round.phase}`} className="space-y-1.5">
                            <p className="text-[9px] font-bold uppercase tracking-widest text-gray-400">
                              {round.phase === "rebuttal" ? "Rebuttals" : "Opening takes"}
                            </p>
                            {round.takes.map((take, idx) => {
                              const meta =
                                AGENT_LABELS[take.agent] || {
                                  label: take.agent,
                                  color: "bg-gray-500/15 text-gray-500 border-gray-500/40",
                                };
                              return (
                                <div
                                  key={`${m.id}-${round.phase}-${idx}`}
                                  className={`px-3 py-2 rounded-xl border ${aiBubble} text-xs leading-relaxed`}
                                >
                                  <span
                                    className={`inline-block text-[9px] font-bold px-1.5 py-0.5 rounded-full border ${meta.color} mr-2`}
                                  >
                                    {meta.label}
                                  </span>
                                  <span className="whitespace-pre-wrap">{take.answer}</span>
                                </div>
                              );
                            })}
                          </div>
                        ) : null,
                      )}
                      {m.fightStatus ? (
                        <p className="text-[10px] text-indigo-500 animate-pulse">
                          {m.fightStatus}
                        </p>
                      ) : (
                        (m.content || "").length > 0 && (
                          <p className="text-[10px] font-bold uppercase tracking-widest text-indigo-500">
                            Anchor's verdict ↓
                          </p>
                        )
                      )}
                    </div>
                  )}

                  {/* Debate panel (legacy): render each specialist's take above the synthesis */}
                  {m.debate && m.debate.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-[10px] font-bold uppercase tracking-widest text-gray-500 dark:text-gray-400 flex items-center gap-1">
                        <ScaleIcon className="w-3 h-3" />
                        Debate · {m.debate.length} specialist takes
                      </p>
                      {m.debate.map((take, idx) => {
                        const meta =
                          AGENT_LABELS[take.agent] || {
                            label: take.agent,
                            color: "bg-gray-500/15 text-gray-500 border-gray-500/40",
                          };
                        return (
                          <div
                            key={`${m.id}-take-${idx}`}
                            className={`px-3 py-2 rounded-xl border ${aiBubble} text-xs leading-relaxed`}
                          >
                            <span
                              className={`inline-block text-[9px] font-bold px-1.5 py-0.5 rounded-full border ${meta.color} mr-2`}
                            >
                              {meta.label}
                            </span>
                            <span className="whitespace-pre-wrap">{take.answer}</span>
                          </div>
                        );
                      })}
                      <p className="text-[10px] font-bold uppercase tracking-widest text-indigo-500">
                        Anchor's synthesis ↓
                      </p>
                    </div>
                  )}

                  {((m.content && m.content.length > 0) || !m.fightMode) && (
                    <div
                      className={`px-3 py-2 rounded-2xl rounded-tl-sm ${aiBubble} whitespace-pre-wrap leading-relaxed`}
                    >
                      {m.content}
                    </div>
                  )}
                  <AgentChips agentsUsed={m.agentsUsed} />
                  <ToolTrail citations={m.citations} />
                </div>
              ),
            )}
            {sending && (
              <div className="max-w-[88%]">
                <div className={`px-3 py-2 rounded-2xl rounded-tl-sm ${aiBubble}`}>
                  <span className="inline-flex gap-1">
                    <span className="w-1.5 h-1.5 bg-current rounded-full animate-bounce" />
                    <span
                      className="w-1.5 h-1.5 bg-current rounded-full animate-bounce"
                      style={{ animationDelay: "0.15s" }}
                    />
                    <span
                      className="w-1.5 h-1.5 bg-current rounded-full animate-bounce"
                      style={{ animationDelay: "0.3s" }}
                    />
                  </span>
                </div>
              </div>
            )}
          </div>

          <form
            onSubmit={onSend}
            className={`p-3 border-t ${border} space-y-2`}
          >
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                disabled={sending}
                placeholder={debate ? "Ask — the Council will debate it…" : "Ask the Council..."}
                className={`flex-1 px-3 py-2 rounded-lg ${inputBg} ${inputBorder} focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60`}
              />
              <button
                type="submit"
                disabled={sending || !input.trim()}
                className="w-9 h-9 rounded-lg bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-400 disabled:cursor-not-allowed flex items-center justify-center text-white transition"
                aria-label="Send"
              >
                <PaperAirplaneIcon className="w-4 h-4" />
              </button>
            </div>
            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={() => setDebate((v) => !v)}
                disabled={sending}
                title="The Council debates automatically when a question is contested. Turn this on to force a full multi-round debate. Uses more tokens."
                className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full border transition ${
                  debate
                    ? "bg-indigo-500/15 text-indigo-500 border-indigo-500/40"
                    : "bg-transparent text-gray-500 border-gray-300 dark:border-gray-700 hover:text-indigo-500 hover:border-indigo-500/40"
                }`}
              >
                <ScaleIcon className="w-3 h-3" />
                Force debate {debate ? "ON" : "OFF"}
              </button>
              {debate && (
                <span className="text-[10px] text-amber-500">
                  Forces a full debate · more tokens · auto-off after one send
                </span>
              )}
            </div>
          </form>
        </div>
      )}
    </>
  );
};

export default ChatWidget;
